/**
 * Prepare quotation: what a quotation in the order system would contain for this case.
 *
 * "Prepare quotation" reads `GET /api/cases/{id}/fo-draft` and shows the checklist (each
 * check is ok or blocked, with the server's sentence), the header, the lines, the lines
 * that are left out and why, the total and the reference.
 *
 * Read with the case: without a decided line the header currency is not set yet, so it
 * says it follows the decided lines (and which currency the priced lines are in); a
 * left-out row names its line and item; a case already answered by a sent quotation
 * says "Already quoted as SQ..." instead of an open checklist.
 *
 * This panel is a preview. The only request it makes is that GET. Creating a quotation
 * is switched off: the create button is always disabled, whatever the answer says, and
 * the closed gates are listed in a collapsed section.
 */

import { Check, CircleSlash, Copy, Icon, Minus } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapsible,
  DataGrid,
  type DataGridColumnDef,
  DescriptionList,
  Skeleton,
  Typography,
} from "@norican/react";
import { type ReactNode, useMemo, useState } from "react";

import { QuotationApprovalSection } from "@/features/case-review/quotation-approval";
import {
  isFieldSource,
  LastQuotationBadge,
  lastQuotationText,
} from "@/features/case-review/quote-history";
import { WhyLink } from "@/features/rules";
import {
  type ApiError,
  type FoCheck,
  type FoDraftHeader,
  type FoDraftLine,
  type FoDraftPreview,
  type FoFreight,
  type FoGate,
  type FoLeftOut,
  sendCaseActivity,
  useFoDraft,
} from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { getFoDraft } from "@/lib/mock";
import { FREIGHT_MODEL_LABEL, SEVERITY_META, toCaseRules } from "@/lib/rules";
import { deliveryTermsNote, DRAFT_CELL_WORDS, linesClipboardText } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { FO_WRITE_NOTE, formatAmount, formatQty, leftOutReasonLabel } from "@/lib/work-labels";

/** True only in the Vite dev server; statically `false` in a production build. */
const DEV_FIXTURES = import.meta.env.DEV;

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw != null && typeof raw === "object" && !Array.isArray(raw);
}

/** The list entries of an untrusted answer that carry the one field the row needs. */
function rows<T>(raw: unknown, key: string): T[] {
  return Array.isArray(raw)
    ? (raw.filter((entry) => isRecord(entry) && typeof entry[key] === "string") as T[])
    : [];
}

/** What a header field the master data cannot give yet shows (quoted by the help). */
export const NOT_AVAILABLE_YET = "Not available yet";

const CHECK_META: Record<
  FoCheck["status"],
  { label: string; tone: "success" | "warning" | "neutral"; icon: typeof Check }
> = {
  ok: { label: "OK", tone: "success", icon: Check },
  blocked: { label: "Open", tone: "warning", icon: CircleSlash },
  not_applicable: { label: "Does not apply", tone: "neutral", icon: Minus },
};

function CheckRow({ check }: { check: FoCheck }) {
  const meta = Object.hasOwn(CHECK_META, check.status)
    ? CHECK_META[check.status]
    : CHECK_META.blocked;
  return (
    <li className="flex items-start gap-2">
      <Badge tone={meta.tone} size="xs" className="mt-0.5 shrink-0">
        <span className="inline-flex items-center gap-1">
          <Icon icon={meta.icon} size="xs" />
          {meta.label}
        </span>
      </Badge>
      <span className="flex min-w-0 flex-col">
        <span className="text-fg text-sm font-semibold break-words">{check.label}</span>
        {check.detail && <span className="text-fg-muted text-xs break-words">{check.detail}</span>}
      </span>
    </li>
  );
}

/**
 * The currency field: the header's; without one (no line decided yet) it follows the
 * decided lines, with the currency the priced lines are in when known.
 */
export function currencyText(
  currency: string | null | undefined,
  pricedCurrency: string | null | undefined,
): string {
  const code = currency?.trim();
  if (code) {
    return code;
  }
  const priced = pricedCurrency?.trim();
  return priced
    ? `Follows the decided lines (${priced} on the priced lines)`
    : "Follows the decided lines";
}

function headerItems(
  header: FoDraftHeader,
  pricedCurrency: string | null | undefined,
): { label: string; value: ReactNode }[] {
  const customer = [header.customer_name, header.customer_account].filter(Boolean).join(" ");
  // header-history lane: the fields whose value came from the customer's last quotation
  const sources = isRecord(header.field_sources) ? header.field_sources : {};
  const entries: [string, string | null | undefined, string?][] = [
    ["Customer", customer],
    ["Company", header.entity ? header.entity.toUpperCase() : null],
    ["Currency", currencyText(header.currency, pricedCurrency)],
    ["Division", header.division],
    ["Product category", header.product_category],
    ["Market type", header.market_type],
    ["Business type", header.business_type],
    ["Sales product", header.sales_product],
    ["Sales pool", header.sales_pool],
    ["Sales unit", header.sales_unit, "sales_unit"],
    ["Sales responsible", header.sales_responsible, "sales_responsible"],
    ["Sales taker", header.sales_taker],
    ["Payment terms", header.payment_terms],
    ["Delivery terms", header.delivery_terms, "delivery_terms"],
    ["Mode of delivery", header.delivery_mode, "delivery_mode"],
    ["Price group", header.price_group, "price_group"],
    [
      "Language",
      header.language
        ? `${header.language}${header.language_source === "entity_default" ? " (company default)" : ""}`
        : null,
    ],
    ["Customer requisition", header.customer_requisition],
    ["Customer reference", header.customer_reference],
  ];
  return entries
    .filter(
      (entry): entry is [string, string, string?] =>
        typeof entry[1] === "string" && entry[1] !== "",
    )
    .map(([label, value, field]) => {
      const source = field ? sources[field] : undefined;
      return {
        label,
        value: isFieldSource(source) ? (
          <span className="flex min-w-0 flex-col gap-0.5">
            <span>{value}</span>
            <LastQuotationBadge source={source} />
          </span>
        ) : (
          value
        ),
      };
    });
}

/** "Last handled by A Person (000456), last quotation SQ…, 02 Jun 2026. …"; null without one. */
export function lastSalesTakerSentence(header: FoDraftHeader): string | null {
  const taker = header.last_sales_taker;
  if (!taker || typeof taker.value !== "string" || !isFieldSource(taker.source)) {
    return null;
  }
  const who = taker.name ? `${taker.name} (${taker.value})` : taker.value;
  return `Last handled by ${who} (${lastQuotationText(taker.source)}). Not used as a default.`;
}

/** What a missing header field is called on the screen. */
const MISSING_FIELD_LABEL: Record<string, string> = {
  sales_unit: "Sales unit",
  sales_responsible: "Sales responsible",
  sales_taker: "Sales taker",
  payment_terms: "Payment terms",
  delivery_terms: "Delivery terms",
  delivery_mode: "Mode of delivery",
  language: "Language",
  credit_block: "Credit block",
  hsn: "HSN code",
};

/** "payment_terms" → "Payment terms"; a known field by its screen name. */
export function missingFieldLabel(field: string): string {
  if (Object.hasOwn(MISSING_FIELD_LABEL, field)) {
    return MISSING_FIELD_LABEL[field] ?? field;
  }
  const words = field.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

/**
 * How the pool and the CRM fields were found, in one sentence; null when the server did
 * not say. Example: "From the item group with most lines: WHE_ST, 3 of 4 lines."
 */
export function headerBasisSentence(header: FoDraftHeader): string | null {
  switch (header.header_basis) {
    case "item_group_count": {
      const group = header.dominant_item_group;
      if (!group) {
        return "From the item group with most lines.";
      }
      const counts = isRecord(header.group_counts) ? Object.values(header.group_counts) : [];
      const total = counts.reduce<number>(
        (sum, value) => sum + (typeof value === "number" && value > 0 ? value : 0),
        0,
      );
      const own = header.group_counts?.[group];
      return typeof own === "number" && total > 0
        ? `From the item group with most lines: ${group}, ${own} of ${total} ${total === 1 ? "line" : "lines"}.`
        : `From the item group with most lines: ${group}.`;
    }
    case "customer_history":
      return "From the customer's earlier quotations, because no item group gives a pool.";
    case "none":
      return "No basis: neither the item groups nor the customer's history give a pool.";
    default:
      return null;
  }
}

const FREIGHT_STATUS_LABEL: Record<NonNullable<FoFreight["status"]>, string> = {
  computed: "Computed",
  not_applicable: "Does not apply",
  exempt: "Customer is exempt",
  manual: "Set by hand",
  incomplete: "Not complete",
  to_confirm: "To confirm",
};

/** The freight block of the preview; null when the server sent none. */
export function FreightBlock({ freight }: { freight: unknown }) {
  if (!isRecord(freight)) {
    return null;
  }
  const quote = freight as FoFreight;
  const status =
    quote.status && Object.hasOwn(FREIGHT_STATUS_LABEL, quote.status) ? quote.status : null;
  const amount =
    quote.amount == null || quote.amount === ""
      ? null
      : `${formatAmount(quote.amount)}${quote.currency ? ` ${quote.currency}` : ""}`;
  const items = [
    ...(status ? [{ label: "Freight", value: FREIGHT_STATUS_LABEL[status] }] : []),
    ...(quote.model && Object.hasOwn(FREIGHT_MODEL_LABEL, quote.model)
      ? [{ label: "Freight model", value: FREIGHT_MODEL_LABEL[quote.model] }]
      : []),
    ...(amount ? [{ label: "Amount", value: amount }] : []),
    ...(quote.charge_code ? [{ label: "Charge code", value: quote.charge_code }] : []),
    ...(quote.band ? [{ label: "Band", value: quote.band }] : []),
  ];
  if (items.length === 0 && !quote.reason) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1.5" data-freight={status ?? "unknown"}>
      <Typography variant="caption" tone="subtle">
        Freight
      </Typography>
      {items.length > 0 && <DescriptionList orientation="horizontal" items={items} />}
      {quote.reason && (
        <Typography variant="body-sm" tone="muted" className="break-words">
          {quote.reason}
        </Typography>
      )}
    </div>
  );
}

function LinesTable({ lines }: { lines: FoDraftLine[] }) {
  const columns = useMemo<DataGridColumnDef<FoDraftLine>[]>(
    () => [
      {
        id: "seq",
        header: "No.",
        width: 48,
        cell: (row) => <span className="tabular-nums">{row.seq}</span>,
      },
      {
        id: "item",
        header: "Item",
        cell: (row) => (
          <span className="block min-w-0">
            <span className="text-fg block font-mono text-xs font-bold break-all">{row.item}</span>
            {row.item_name && (
              <span className="text-fg-muted block text-xs break-words">{row.item_name}</span>
            )}
          </span>
        ),
      },
      {
        id: "qty",
        header: "Quantity",
        width: 84,
        align: "end",
        cell: (row) =>
          row.qty == null ? (
            <span className="text-fg-subtle text-xs" data-qty-missing>
              {DRAFT_CELL_WORDS.qtyMissing}
            </span>
          ) : (
            <span className="tabular-nums">{formatQty(row.qty)}</span>
          ),
      },
      {
        id: "unit",
        header: "Unit",
        width: 132,
        // The unit the customer asked in stays visible; a conversion shows its source,
        // a unit without a verified conversion shows why (F03).
        cell: (row) => (
          <span className="flex min-w-0 flex-col" data-unit-issue={row.unit_issue ? "" : undefined}>
            <span>{row.unit ?? <span className="text-fg-subtle">-</span>}</span>
            {row.conversion && (
              <span className="text-fg-muted text-xs break-words">{row.conversion}</span>
            )}
            {row.unit_issue && (
              <span className="text-warning-fg text-xs break-words">{row.unit_issue}</span>
            )}
          </span>
        ),
      },
      {
        id: "price",
        header: "Price",
        width: 100,
        align: "end",
        cell: (row) =>
          row.unit_price == null ? (
            <span className="text-fg-subtle text-xs">no price</span>
          ) : (
            <span className="flex flex-col items-end">
              <span className="tabular-nums">{formatAmount(row.unit_price)}</span>
              {row.price_uom && row.price_uom !== row.unit && (
                <span className="text-fg-muted text-xs">per {row.price_uom}</span>
              )}
            </span>
          ),
      },
      {
        id: "currency",
        header: "Currency",
        width: 84,
        cell: (row) => row.currency ?? <span className="text-fg-subtle">-</span>,
      },
    ],
    [],
  );
  return (
    <div className="overflow-x-auto">
      <DataGrid<FoDraftLine>
        columns={columns}
        data={lines}
        getRowId={(row) => `${row.seq}-${row.item}`}
        density="compact"
        tableLayout="fixed"
        className="min-w-[600px]"
        aria-label="Lines of the prepared quotation"
      />
    </div>
  );
}

function GatesSection({ gates }: { gates: FoGate[] }) {
  const [open, setOpen] = useState(false);
  if (gates.length === 0) {
    return null;
  }
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
        {open ? "Hide what keeps this switched off" : "Show what keeps this switched off"}
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <ul className="mt-2 flex flex-col gap-1.5">
          {gates.map((gate) => (
            <li key={gate.code} className="flex items-start gap-2">
              <Badge tone="neutral" variant="outline" size="xs" className="mt-0.5 shrink-0">
                {gate.code} closed
              </Badge>
              <span className="flex min-w-0 flex-col">
                <span className="text-fg text-xs font-semibold break-words">{gate.label}</span>
                {gate.detail && (
                  <span className="text-fg-muted text-xs break-words">{gate.detail}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/**
 * "Copy lines": item, quantity, unit, sales price and currency of the prepared lines,
 * tab separated, to paste into a grid such as Excel. Whether F&O takes the pasted rows
 * in its quotation grid is not checked yet, so the hint says to check before use there.
 */
function CopyLinesButton({ caseId, lines }: { caseId: string; lines: FoDraftLine[] }) {
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(linesClipboardText(lines));
      setCopied("yes");
      logger.debug("[case] lines copied", { case: caseId, lines: lines.length });
      void sendCaseActivity(caseId, { kind: "lines_copy" });
      track("case.lines_copy", { lines: lines.length }, { caseId });
    } catch (error) {
      logger.warn("[case] copy lines failed", error);
      setCopied("failed");
    }
  };
  return (
    <span className="ml-auto flex flex-wrap items-center gap-2">
      {copied === "yes" && (
        <Typography as="span" variant="caption" tone="muted" role="status">
          Copied. Paste into Excel, or check the rows before you use them in F&amp;O.
        </Typography>
      )}
      {copied === "failed" && (
        <Typography as="span" variant="caption" className="text-danger-fg" role="alert">
          Copy did not work. Mark the lines and copy them yourself.
        </Typography>
      )}
      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        startIcon={<Icon icon={Copy} size="xs" />}
        onClick={() => void copy()}
      >
        Copy lines
      </Button>
    </span>
  );
}

/** What the case page knows that the preview does not: line numbers, items, currency. */
export interface FoCaseContext {
  /** "Line 2" for a line key; null when not known. */
  lineLabel?: (lineKey: string) => string | null;
  /** The item of a line (decided, matched or suggested); null when none. */
  itemOf?: (lineKey: string) => string | null;
  /** The currency of the priced lines, when they agree on one. */
  pricedCurrency?: string | null;
  /** True when a sent quotation already answers the case: no open checklist. */
  quoted?: boolean;
  /** The Incoterms the mail names (`header_fields.incoterms`), for the delivery terms note. */
  mailIncoterms?: string | null;
}

/** "Line 2, item 099-1234-56: Not decided yet", or the reason alone without a key. */
export function leftOutText(line: FoLeftOut, context: FoCaseContext = {}): string {
  const reason = leftOutReasonLabel(line.reason);
  const key = line.line_key;
  const name = key ? context.lineLabel?.(key) : null;
  const item = key ? context.itemOf?.(key) : null;
  const which = [name, item ? `item ${item}` : null].filter(Boolean).join(", ");
  return which ? `${which}: ${reason}` : reason;
}

/** The preview itself, from an untrusted answer: every part is optional. */
export function FoDraftView({
  preview,
  context = {},
}: {
  preview: FoDraftPreview;
  context?: FoCaseContext;
}) {
  const checks = rows<FoCheck>(preview.checks, "label");
  const lines = rows<FoDraftLine>(preview.lines, "item");
  const leftOut = rows<FoLeftOut>(preview.left_out, "enquiry_line");
  const gates = rows<FoGate>(preview.gates, "code");
  const header = isRecord(preview.header)
    ? headerItems(preview.header, lines.length === 0 ? context.pricedCurrency : null)
    : [];
  const basis = isRecord(preview.header) ? headerBasisSentence(preview.header) : null;
  const takerSentence = isRecord(preview.header) ? lastSalesTakerSentence(preview.header) : null;
  // Hunt round 3, Q-22: the mail and the customer master name other Incoterms.
  const termsNote = isRecord(preview.header)
    ? deliveryTermsNote(
        context.mailIncoterms,
        typeof preview.header.delivery_terms === "string" ? preview.header.delivery_terms : null,
      )
    : null;
  const missing =
    isRecord(preview.header) && Array.isArray(preview.header.missing_fields)
      ? preview.header.missing_fields.filter((f): f is string => typeof f === "string" && f !== "")
      : [];
  // Reasons about the header (pool, missing fields); the case page lists all of them.
  const headerReasons = (toCaseRules(preview.rules)?.reasons ?? []).filter(
    (reason) => reason.scope === "header",
  );
  const blocked = checks.filter((check) => check.status === "blocked").length;
  const note =
    typeof preview.write_note === "string" && preview.write_note.trim() !== ""
      ? preview.write_note.trim()
      : FO_WRITE_NOTE;
  const total = typeof preview.total === "number" ? preview.total : null;

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="info" title="Preview only">
        {context.quoted
          ? "For reference only: sales already sent a quotation for this case. Nothing is sent."
          : preview.ready === true
            ? "Every check passed. This is what the quotation would contain. Nothing is sent: writing to F&O is switched off in this version."
            : blocked > 0
              ? `${blocked} ${blocked === 1 ? "step is" : "steps are"} still open. These are the checks a quotation would have to pass. Nothing is sent to the order system: writing to F&O is switched off in this version.`
              : "The quotation is not complete yet. See the list below."}
      </Alert>

      {checks.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Typography variant="caption" tone="subtle">
            Checks
          </Typography>
          <ul className="flex flex-col gap-2">
            {checks.map((check) => (
              <CheckRow key={check.code ?? check.label} check={check} />
            ))}
          </ul>
        </div>
      )}

      {(header.length > 0 || missing.length > 0) && (
        <div className="flex flex-col gap-1.5">
          <Typography variant="caption" tone="subtle">
            Header
          </Typography>
          {header.length > 0 && <DescriptionList orientation="horizontal" items={header} />}
          {basis && (
            <Typography variant="caption" tone="muted" className="break-words">
              {basis}
            </Typography>
          )}
          {takerSentence && (
            <Typography variant="caption" tone="muted" className="break-words">
              {takerSentence}
            </Typography>
          )}
          {termsNote && (
            <Alert tone="warning" data-delivery-terms-note="">
              {termsNote}
            </Alert>
          )}
          {missing.length > 0 && (
            <DescriptionList
              orientation="horizontal"
              aria-label="Header fields not available yet"
              items={missing.map((field) => ({
                label: missingFieldLabel(field),
                value: <span className="text-fg-subtle">{NOT_AVAILABLE_YET}</span>,
              }))}
            />
          )}
          {headerReasons.length > 0 && (
            <ul className="flex flex-col gap-1">
              {headerReasons.map((reason, index) => (
                <li
                  key={`${reason.rule_id}-${index}`}
                  className="flex min-w-0 items-start gap-2"
                  data-rule={reason.rule_id}
                >
                  <Badge tone={SEVERITY_META[reason.severity].tone} size="xs" className="mt-0.5">
                    {SEVERITY_META[reason.severity].label}
                  </Badge>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-fg text-xs break-words">{reason.message}</span>
                    {reason.action && (
                      <span className="text-fg-muted text-xs break-words">{reason.action}</span>
                    )}
                  </span>
                  <WhyLink
                    ruleId={reason.rule_id}
                    entity={reason.entity || preview.entity}
                    severity={reason.severity}
                    className="ml-auto h-auto shrink-0 px-1 py-0.5 text-xs"
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="caption" tone="subtle">
            Lines
          </Typography>
          {FEATURES.copyReady && lines.length > 0 && (
            <CopyLinesButton caseId={preview.case_id} lines={lines} />
          )}
        </div>
        {lines.length > 0 ? (
          <LinesTable lines={lines} />
        ) : (
          <Typography variant="body-sm" tone="muted">
            No line is ready for a quotation yet.
          </Typography>
        )}
      </div>

      <FreightBlock freight={preview.freight} />

      {leftOut.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Typography variant="caption" tone="subtle">
            Left out
          </Typography>
          <ul className="flex flex-col gap-1">
            {leftOut.map((line, index) => (
              <li key={line.line_key ?? index} className="flex min-w-0 flex-col text-sm">
                <span className="text-fg break-words">{line.enquiry_line}</span>
                <span className="text-fg-muted text-xs">{leftOutText(line, context)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(total != null || preview.reference) && (
        <DescriptionList
          orientation="horizontal"
          items={[
            ...(total != null
              ? [
                  {
                    label: "Total",
                    value: `${formatAmount(total)}${preview.currency ? ` ${preview.currency}` : ""}`,
                  },
                ]
              : []),
            ...(preview.reference
              ? [
                  {
                    label: "Reference",
                    value: <span className="font-mono break-all">{preview.reference}</span>,
                  },
                ]
              : []),
          ]}
        />
      )}

      <div className="border-border-subtle flex flex-col gap-2 border-t pt-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Always disabled: no request exists behind this button. */}
          <Button size="sm" tone="accent" disabled aria-disabled="true">
            Create quotation in the order system
          </Button>
          <Typography variant="caption" tone="muted" className="min-w-0 break-words">
            {note}
          </Typography>
        </div>
        <GatesSection gates={gates} />
      </div>
    </div>
  );
}

/**
 * The panel in the case view. Asks for nothing until "Prepare quotation" is pressed.
 *
 * Stage 4: under the preview, `QuotationApprovalSection` (its own file) records a sales person's
 * approval in the app database. This file still makes no request other than the GET.
 */
export function FoDraftPanel({
  caseId,
  className,
  lineIndexOf,
  context,
  alreadyQuoted,
}: {
  caseId: string;
  className?: string;
  /** The position of a line in the stored draft by its key (for a price approval). */
  lineIndexOf?: (lineKey: string) => number | undefined;
  /** Line numbers, items and the priced currency from the case page. */
  context?: FoCaseContext;
  /** "SQ0069260 (sent)" when a sent quotation already answers this case; else null. */
  alreadyQuoted?: string | null;
}) {
  const [asked, setAsked] = useState(false);
  const draft = useFoDraft(caseId, asked);
  const status = (draft.error as ApiError | null)?.status;
  // Local dev without a backend: the sample preview, marked as such.
  const sample = DEV_FIXTURES && draft.isError && status !== 404 ? getFoDraft() : null;
  const preview = draft.data ?? sample;

  const prepare = () => {
    logger.debug("[case] prepare quotation", { case: caseId, again: asked });
    if (asked) {
      void draft.refetch();
    } else {
      setAsked(true);
    }
  };

  return (
    <Card variant="outline" padding="none" className={className}>
      <Card.Header padding="sm" className="border-border-subtle flex items-center gap-2 border-b">
        <Typography variant="label">Quotation for the order system</Typography>
        {sample && !draft.data && (
          <Badge tone="warning" variant="outline" size="sm">
            sample data (local dev)
          </Badge>
        )}
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          className="ml-auto"
          loading={draft.isFetching}
          onClick={prepare}
        >
          {asked && preview ? "Check again" : "Prepare quotation"}
        </Button>
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        {alreadyQuoted && (
          <Alert tone="info" title={`Already quoted as ${alreadyQuoted}`}>
            Sales replied to this inquiry with that quotation. There is nothing to prepare.
          </Alert>
        )}
        {!asked ? (
          alreadyQuoted ? null : (
            <Typography variant="body-sm" tone="muted">
              Shows what a quotation for this case would contain and what is still missing. Nothing
              is created or sent.
            </Typography>
          )
        ) : draft.isLoading ? (
          <Skeleton variant="rect" height={96} />
        ) : !preview ? (
          <Typography variant="body-sm" tone="muted" role="alert">
            {status === 404
              ? "This is not available for this case."
              : "I2Q could not prepare the quotation now. Try again in a moment."}
          </Typography>
        ) : (
          <>
            {/* A case already quoted shows no open checklist: only the lines, for reference. */}
            <FoDraftView
              preview={alreadyQuoted ? { ...preview, checks: [], gates: [] } : preview}
              context={{ ...context, quoted: Boolean(alreadyQuoted) }}
            />
            {draft.data && (
              <QuotationApprovalSection
                caseId={caseId}
                preview={draft.data}
                lineIndexOf={lineIndexOf}
              />
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
